import FileSaver from "file-saver";
import * as queryString from "query-string";
import * as Config from "../config";
import { t } from "../i18n/util";
import { authStore } from "../stores/AuthStore";
import { generalStore } from "../stores/GeneralStore";
import { STATUS_CODE_NOT_ALLOWED, STATUS_CODE_UNAUTHORIZED } from "./API";
import * as NetworkStapler from "./NetworkStapler";
import { IAPITarget, IDownloadAPITarget, urljoin } from "./NetworkStapler";

const injectHeaders = (target: NetworkStapler.IAPITarget) => {
    const ret: Record<string, string> = {
        ...target.headers,
        "Content-Type": target.headers?.["Content-Type"] ?? "application/json",
        Accept: "application/json"
        // "Accept-Language": authStore.locale,
    };

    if (authStore.credentials?.access_token) {
        ret.Authorization = `Bearer ${authStore.credentials?.access_token}`;
    }

    return ret;
};

class APIClient {
    apiClient: NetworkStapler.APIClient;

    constructor() {
        this.apiClient = new NetworkStapler.APIClient({
            baseUrl: Config.API_BASE_URL,
            injectHeaders: injectHeaders,
            throwOnErrorStatusCodes: true
        });
    }

    async request(target: NetworkStapler.IAPITarget): Promise<Response> {
        try {
            return await this.apiClient.request(target);
        } catch (err) {
            const retry = await this.checkStatusCodeError(err);
            if (retry) {
                return await this.apiClient.request(target);
            }
            throw err;
        }
    }

    async requestJSON(target: NetworkStapler.IAPITarget): Promise<object> {
        try {
            return await this.apiClient.requestJSON(target);
        } catch (err) {
            const retry = await this.checkStatusCodeError(err);
            if (retry) {
                return await this.apiClient.requestJSON(target);
            }
            throw err;
        }
    }

    async requestType<T>(target: NetworkStapler.ITypedAPITarget<T>, handleRefresh = true): Promise<T> {
        try {
            return await this.apiClient.requestType(target);
        } catch (err) {
            const retry = await this.checkStatusCodeError(err, handleRefresh);
            if (retry) {
                return await this.apiClient.requestType(target);
            }
            throw err;
        }
    }

    async uploadFormData(target: IAPITarget) {
        const headers = injectHeaders(target);

        // FormData with fetch must not have Content-Type set. This gets set automatically by
        // fetch()
        delete headers["Content-Type"];

        let requestUrl = urljoin(this.apiClient.options.baseUrl, target.url);

        if (target.queryParameters) {
            const query = queryString.stringify(target.queryParameters);
            if (query) {
                requestUrl = urljoin(requestUrl, `?${query}`);
            }
        }

        const myOptions: any = {
            method: target.method || "GET",
            body: target.body,
            headers: headers
        };
        const response = await fetch(requestUrl, myOptions);
        if (this.apiClient.options.throwOnErrorStatusCodes) {
            if (response.status >= 200 && response.status < 400) {
                return response;
            }
            throw new NetworkStapler.APIClientStatusCodeError(response, response.status);
        }

        return response;
    }

    async uploadFormDataJSON(target: IAPITarget): Promise<object> {
        return (await this.uploadFormData(target)).json();
    }

    async downloadFile(target: IDownloadAPITarget) {
        try {
            const res = await this.apiClient.request(target);
            const blob = await res.blob();
            FileSaver.saveAs(blob, target.fileName);
        } catch (err) {
            const retry = await this.checkStatusCodeError(err);
            if (retry) {
                return await this.apiClient.request(target);
            }
            throw err;
        }
    }

    async checkStatusCodeError(error: any, handleRefresh?: boolean): Promise<boolean> {
        let retry = false;
        if (error instanceof NetworkStapler.APIClientStatusCodeError) {
            if (error.statusCode) {
                switch (error.statusCode) {
                    case STATUS_CODE_UNAUTHORIZED:
                        if (handleRefresh !== false) {
                            retry = await authStore.handleUnauthorized();
                        }
                        break;
                    case STATUS_CODE_NOT_ALLOWED:
                        generalStore.setError(t("error.notAllowed"), error);
                        break;
                    default:
                        break;
                }
            }
        }

        return retry;
    }
}

export const apiClient = new APIClient();
